import { ErrorBoundary } from '../shared/ErrorBoundary';
import { useTeam } from '../../hooks/useTeam';
import { useState } from 'react';
import { Button } from '../ui/button';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableHead, 
  TableRow, 
  TableCell 
} from '../ui/table';
import { useKYPSearch } from '../../lib/kyp-search';
import { NewsButton } from '../news/NewsButton';
import { SlidePanel } from '../ui/slide-panel';
import { PlayerNewsPanel } from '../news/PlayerNewsPanel';
import { useInjuryRequestService } from '../../lib/injury-request.service';

type Player = {
  id: string;
  name: string;
  number: number;
  photoUrl: string;
  bio: {
    position: string;
    age: number;
    nationality: string;
  };
  stats?: {
    physicalAttributes?: {
      height?: number;
      weight?: number;
      preferredFoot?: string;
    };
  };
  injuries?: {
    active?: boolean;
    details?: string;
  };
};

export const TeamPanel = () => {
  const { data: teamData, isLoading, error } = useTeam();
  const { playerInjuries, playerProfile } = useKYPSearch();
  const { requestInjuryInfo } = useInjuryRequestService();
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [requestingInjuryInfo, setRequestingInjuryInfo] = useState<string | null>(null);
  
  // State for News dialog
  const [selectedPlayer, setSelectedPlayer] = useState<{ id: string; name: string } | null>(null);
  const [isNewsDialogOpen, setIsNewsDialogOpen] = useState(false);
  
  // Handle News button click
  const handleNewsClick = (playerId: string, playerName: string) => {
    setSelectedPlayer({ id: playerId, name: playerName });
    setIsNewsDialogOpen(true);
  };
  
  // Function to check if a player has an active injury
  const checkActiveInjury = (playerId: string) => {
    if (!playerInjuries) return null;
    
    // Log for debugging
    console.log('Checking injuries for player:', playerId);
    console.log('All injuries:', playerInjuries);
    
    const activeInjuries = playerInjuries.filter(
      injury => injury.player_id === playerId && 
      (!injury.until_date || new Date(injury.until_date) > new Date())
    );
    
    console.log('Active injuries found:', activeInjuries);
    
    if (activeInjuries.length === 0) return null;
    
    // Get the first active injury date
    const injuryDate = activeInjuries.length > 0 
      ? new Date(activeInjuries[0].from_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : '';
    
    return {
      active: true,
      details: activeInjuries.map(injury => injury.injury).join(', '),
      date: injuryDate,
      injury: activeInjuries[0] // Return the first injury object for reference
    };
  };
  
  // Handle requesting more information about an injury
  const handleRequestInjuryInfo = async (playerId: string, playerName: string) => {
    const injury = checkActiveInjury(playerId);
    if (!injury) return;
    
    setRequestingInjuryInfo(playerId);
    try {
      await requestInjuryInfo(
        playerId,
        playerName,
        playerProfile?.club_name || 'Unknown',
        injury.details,
        injury.date
      );
    } finally {
      setRequestingInjuryInfo(null);
    }
  };

  if (isLoading) {
    return (
      <ErrorBoundary>
        <div className="bg-[#121212] rounded-lg shadow-lg p-4 transition-colors h-full flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-[#CECFD2]">Team</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 flex-grow">
            {[...Array(15)].map((_, index) => (
              <div key={index} className="animate-pulse bg-[#26222E] rounded-md overflow-hidden aspect-[3/4]">
                <div className="h-[40%] bg-[#26222E]/80"></div>
                <div className="h-[60%] p-2 bg-[#26222E] flex flex-col justify-between">
                  <div className="space-y-1 text-center">
                    <div className="h-2 bg-[#26222E]/80 rounded w-1/2 mx-auto"></div>
                    <div className="h-1.5 bg-[#26222E]/80 rounded w-1/3 mx-auto"></div>
                  </div>
                  <div className="space-y-1">
                    <div className="h-1.5 bg-[#26222E]/80 rounded w-1/4 mx-auto"></div>
                    <div className="h-0.5 bg-[#26222E]/80 rounded w-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <div className="bg-[#121212] rounded-lg shadow-lg p-4 transition-colors h-full flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-[#CECFD2]">Team</h2>
          <div className="flex-grow flex items-center justify-center">
            <p className="text-red-500">Error loading team data</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (!teamData?.players || teamData.players.length === 0) {
    return (
      <ErrorBoundary>
        <div className="bg-[#121212] rounded-lg shadow-lg p-4 transition-colors h-full flex flex-col">
          <h2 className="text-xl font-bold mb-4 text-[#CECFD2]">Team</h2>
          <div className="flex-grow flex items-center justify-center">
            <p className="text-[#CECFD2]">No team data available</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const renderPlayerCard = (player: Player) => {
    // Check if player has an active injury
    const injury = checkActiveInjury(player.id);
    
    return (
      <div 
        key={player.id} 
        className={`border rounded-lg min-h-[280px] transition-all duration-200 transform-gpu ${
          injury?.active 
            ? 'border-red-500' 
            : 'border-gray-500'
        } hover:bg-[#26222E] group`}
        style={{ transformOrigin: 'center center' }}
      >
        <div className="h-[140px] relative p-2">
          <div className="relative w-full h-full">
            <img
              src={player.photoUrl}
              alt={player.name}
              className="w-full h-full object-contain object-center"
            />
            {player.number > 0 && (
              <div className="absolute top-1 left-1 bg-[#4E46FF] text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-md">
                {player.number}
              </div>
            )}
            
            {/* News Button */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <NewsButton 
                playerId={player.id}
                playerName={player.name}
                size="sm"
                onClick={() => handleNewsClick(player.id, player.name)}
              />
            </div>
            
            {/* Active Injury Badge */}
            {injury?.active && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white px-1.5 py-0.5 rounded text-xs font-bold shadow-md">
                INJURY
              </div>
            )}
          </div>
        </div>
        <div className="min-h-[140px] bg-transparent flex flex-col p-3 gap-2">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-[#CECFD2] group-hover:text-[#4E46FF]">
              {player.name.includes(' ') 
                ? `${player.name.split(' ')[0][0]}. ${player.name.split(' ').slice(-1)[0]}`
                : player.name
              }
            </h3>
            <div className="text-[#CECFD2] text-sm mt-1">
              {player.bio.position}
            </div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-sm">
              <span className="text-[#CECFD2]">
                {player.bio.nationality} • {player.bio.age}y
              </span>
            </div>
            
            {/* Active Injury Details */}
            {injury?.active && injury.details && (
              <div className="mt-2 space-y-1">
                <div className="px-2 py-1 bg-red-900/30 text-red-200 rounded text-xs">
                  <span className="font-semibold">Active Injury:</span> {injury.details}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRequestInjuryInfo(player.id, player.name);
                  }}
                  disabled={requestingInjuryInfo === player.id}
                  className={`w-full px-2 py-1 rounded text-xs text-white transition-colors ${
                    requestingInjuryInfo === player.id 
                      ? 'bg-[#26222E] opacity-50' 
                      : 'bg-[#4E46FF] hover:bg-[#3d37cc]'
                  }`}
                >
                  {requestingInjuryInfo === player.id ? 'Requesting...' : 'Request More Information'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const renderPlayerTable = () => (
    <Table className="border-collapse">
      <TableHeader>
        <TableRow className="bg-[#26222E] border-b border-gray-700">
          <TableHead className="text-[#CECFD2] font-bold">#</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Player</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Position</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Age</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Nationality</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Height</TableHead>
          <TableHead className="text-[#CECFD2] font-bold">Foot</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {teamData?.players.map((player) => {
          // Check if player has an active injury
          const injury = checkActiveInjury(player.id);
          
          return (
            <TableRow 
              key={player.id}
              className={`border-b border-gray-700 hover:bg-[#26222E] ${
                injury?.active ? 'bg-red-900/20' : ''
              } group`}
            >
              <TableCell className="text-[#CECFD2] font-medium">
                {player.number > 0 ? player.number : '-'}
              </TableCell>
              <TableCell className="text-[#CECFD2]">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full overflow-hidden relative">
                    <img 
                      src={player.photoUrl} 
                      alt={player.name} 
                      className="w-full h-full object-cover"
                    />
                    {/* Injury indicator if player is injured */}
                    {injury?.active && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                        !
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="group-hover:text-[#4E46FF]">{player.name}</span>
                    {/* News Button below name on hover */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 mt-1">
                      <NewsButton 
                        playerId={player.id}
                        playerName={player.name}
                        size="sm"
                        onClick={() => handleNewsClick(player.id, player.name)}
                      />
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-[#CECFD2]">{player.bio.position}</TableCell>
              <TableCell className="text-[#CECFD2]">{player.bio.age}</TableCell>
              <TableCell className="text-[#CECFD2]">{player.bio.nationality}</TableCell>
              <TableCell className="text-[#CECFD2]">
                {player.stats?.physicalAttributes?.height && player.stats.physicalAttributes.height > 0 
                  ? `${player.stats.physicalAttributes.height}cm` 
                  : '-'}
              </TableCell>
              <TableCell className="text-[#CECFD2]">
                {player.stats?.physicalAttributes?.preferredFoot ? 
                  player.stats.physicalAttributes.preferredFoot.charAt(0).toUpperCase() + 
                  player.stats.physicalAttributes.preferredFoot.slice(1) 
                  : '-'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <ErrorBoundary>
      <div className="bg-[#121212] rounded-lg shadow-lg p-4 transition-colors max-h-[75vh] flex flex-col">
        {/* News Slide Panel */}
        {selectedPlayer && (
          <SlidePanel
            isOpen={isNewsDialogOpen}
            onClose={() => {
              setIsNewsDialogOpen(false);
              setSelectedPlayer(null);
            }}
            title={`${selectedPlayer.name} News`}
            width="max-w-xl"
          >
            <PlayerNewsPanel 
              playerId={selectedPlayer.id} 
              playerName={selectedPlayer.name}
            />
          </SlidePanel>
        )}
        
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-[#CECFD2]">Team</h2>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => setViewMode('card')}
              className={`px-3 py-1 h-8 ${
                viewMode === 'card' 
                  ? 'bg-[#4E46FF] hover:bg-[#3d37cc] text-white' 
                  : 'bg-[#26222E] text-[#CECFD2] opacity-50'
              }`}
              aria-label="Card view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            </Button>
            <Button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 h-8 ${
                viewMode === 'table' 
                  ? 'bg-[#4E46FF] hover:bg-[#3d37cc] text-white' 
                  : 'bg-[#26222E] text-[#CECFD2] opacity-50'
              }`}
              aria-label="Table view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto flex-grow scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 pr-2">
          {viewMode === 'card' ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {teamData.players.map(renderPlayerCard)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {renderPlayerTable()}
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};